// Booking-pass controls for #2691: the owner's "Request a move" row with the
// club's reschedule policy, and the Confirm / "Can't make it" prompt a player
// sees after the club moves the booking to a new time.

import { useRef, useState, type ReactNode } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ListItem,
  ListItemButton,
  Stack,
  Typography,
} from "@mui/material";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import { isApiError } from "@bw/shared/api";
import { useQueryClient } from "@tanstack/react-query";
import {
  MOVE_ERROR,
  newIdempotencyKey,
  type OwnChangeRequest,
} from "@bw/shared/calendar/contract";

import {
  moveRequestKeys,
  outsideCutoff,
  useOwnChangeRequests,
  useReconfirm,
  useReconfirmStatus,
  useReschedulePolicy,
} from "../hooks/useMoveRequest";
import { RequestMoveSheet, type MovableBooking } from "./RequestMoveSheet";

function requestedSlotLabel(request: OwnChangeRequest, timezone: string): string {
  try {
    const day = new Intl.DateTimeFormat(undefined, {
      timeZone: timezone,
      weekday: "short",
      month: "short",
      day: "numeric",
    }).format(new Date(request.proposed_start_time));
    const time = new Intl.DateTimeFormat(undefined, {
      timeZone: timezone,
      hour: "numeric",
      minute: "2-digit",
    });
    return `${day}, ${time.format(new Date(request.proposed_start_time))} – ${time.format(new Date(request.proposed_end_time))}`;
  } catch {
    return "a new time";
  }
}

/**
 * "Request a move" for the booking owner of a confirmed, future booking.
 * Inside the club's cutoff the row is disabled and says why; the policy text
 * is always shown under it. While a request is pending, a "Move requested"
 * row shows the proposed time and a new request is not offered.
 */
export function RequestMoveRow({
  booking,
  timezone,
  tile,
}: {
  booking: MovableBooking;
  timezone: string;
  tile: ReactNode;
}) {
  const client = useQueryClient();
  const policyQuery = useReschedulePolicy(booking.facility_id);
  const requestsQuery = useOwnChangeRequests(booking.booking_id, true);
  const [open, setOpen] = useState(false);
  const policy = policyQuery.data ?? null;
  const pending =
    requestsQuery.data?.find((request) => request.state === "pending") ?? null;
  const allowed =
    !pending &&
    (policy
      ? outsideCutoff(booking.start_time, policy.reschedule_request_cutoff_hours)
      : false);
  const subtitle = !policy
    ? policyQuery.isError
      ? "Message the club to change this booking."
      : "Checking the club's policy…"
    : pending
      ? "You already asked to move this booking."
      : allowed
        ? "Pick a new time. The club approves every move."
        : `Requests close ${policy.reschedule_request_cutoff_hours} hours before start.`;

  return (
    <>
      {pending ? (
        <ListItem
          aria-label={`Move requested, ${requestedSlotLabel(pending, timezone)}, awaiting the club`}
          sx={{ gap: 1.5, px: { xs: 0.5, sm: 1 }, py: 0.75 }}
        >
          {tile}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600 }}>
              Move requested
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {requestedSlotLabel(pending, timezone)} · Awaiting club
            </Typography>
          </Box>
        </ListItem>
      ) : null}
      <ListItem disablePadding>
        <ListItemButton
          aria-label={`Request a move, ${subtitle}`}
          disabled={!allowed}
          onClick={() => setOpen(true)}
          sx={{ gap: 1.5, px: { xs: 0.5, sm: 1 }, py: 0.75, borderRadius: "8px" }}
        >
          {tile}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600 }}>
              Request a move
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {subtitle}
            </Typography>
          </Box>
          {allowed ? <ChevronRightRounded color="action" aria-hidden /> : null}
        </ListItemButton>
      </ListItem>
      {policy ? (
        <Typography
          color="text.secondary"
          component="p"
          variant="caption"
          sx={{ px: { xs: 0.5, sm: 1 }, pb: 0.5 }}
        >
          {policy.policy_text}
        </Typography>
      ) : null}
      <RequestMoveSheet
        booking={booking}
        onClose={() => setOpen(false)}
        onSent={() => {
          setOpen(false);
          void client.invalidateQueries({
            queryKey: moveRequestKeys.ownRequests(booking.booking_id),
          });
        }}
        open={open}
        policyText={policy?.policy_text ?? null}
        timezone={timezone}
      />
    </>
  );
}

/**
 * After the club moves a booking to a new date, time or length, each player
 * confirms or says they can't make it. Silence counts as still coming, so the
 * prompt only appears while the server says this player's answer is pending.
 */
export function ReconfirmPrompt({
  bookingId,
  startTime,
}: {
  bookingId: string;
  startTime: string;
}) {
  const beforeStart = Date.parse(startTime) > Date.now();
  const status = useReconfirmStatus(bookingId, beforeStart);
  const reconfirm = useReconfirm(bookingId);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [closed, setClosed] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  // One key per answer: retrying after a network error resends the same
  // answer instead of recording a second one.
  const answerKeys = useRef(new Map<"confirm" | "cant_make_it", string>());

  const answer = (response: "confirm" | "cant_make_it") => {
    setFailure(null);
    const key = answerKeys.current.get(response) ?? newIdempotencyKey();
    answerKeys.current.set(response, key);
    reconfirm.mutate(
      { response, idempotency_key: key },
      {
        onSettled: () => setConfirmLeave(false),
        onSuccess: () => answerKeys.current.delete(response),
        onError: (error) => {
          if (isApiError(error) && error.code === MOVE_ERROR.reconfirmClosed) {
            setClosed(true);
            return;
          }
          setFailure("We couldn't send your answer. Try again.");
        },
      },
    );
  };

  if (closed) {
    return (
      <Alert severity="info">
        This booking can no longer be confirmed: it has started, you&apos;ve
        checked in, or it changed again.
      </Alert>
    );
  }
  const current = reconfirm.data?.participant_status ?? status.data?.participant_status;
  if (current === "confirmed") {
    return <Alert severity="success">Thanks, you&apos;re confirmed for the new time.</Alert>;
  }
  if (current === "cant_make_it") {
    return (
      <Alert severity="info">
        You&apos;re off this booking. Your share is refunded in full.
      </Alert>
    );
  }
  if (!beforeStart || current !== "pending") return null;

  return (
    <Alert
      icon={false}
      severity="warning"
      slotProps={{ message: { sx: { width: "100%" } } }}
      sx={{ borderRadius: "12px" }}
    >
      <Stack spacing={1}>
        <Typography sx={{ fontWeight: 700 }}>The club moved this booking</Typography>
        <Typography variant="body2">
          Can you still make the new time? If we don&apos;t hear from you,
          we&apos;ll count you in.
        </Typography>
        {failure ? <Typography variant="body2">{failure}</Typography> : null}
        <Stack direction="row" spacing={1}>
          <Button
            disabled={reconfirm.isPending}
            onClick={() => answer("confirm")}
            size="small"
            variant="contained"
          >
            Confirm
          </Button>
          <Button
            disabled={reconfirm.isPending}
            onClick={() => setConfirmLeave(true)}
            size="small"
            variant="outlined"
          >
            Can&apos;t make it
          </Button>
        </Stack>
      </Stack>
      <Dialog
        aria-labelledby="reconfirm-leave-title"
        onClose={reconfirm.isPending ? undefined : () => setConfirmLeave(false)}
        open={confirmLeave}
      >
        <DialogTitle id="reconfirm-leave-title">Leave this booking?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You&apos;ll be taken off the booking and your share is refunded in
            full. The booking owner is told.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disabled={reconfirm.isPending} onClick={() => setConfirmLeave(false)}>
            Stay on it
          </Button>
          <Button
            color="error"
            disabled={reconfirm.isPending}
            onClick={() => answer("cant_make_it")}
            variant="contained"
          >
            Can&apos;t make it
          </Button>
        </DialogActions>
      </Dialog>
    </Alert>
  );
}
